import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useApi, identityApiRef, configApiRef } from '@backstage/core-plugin-api';

// Define the interface for the user role response from your API
interface UserRoleResponse {
  userID: string;
  roleID: string;
  roleName: string;
}

// Define the interface for the context value
interface UserContextProps {
  email: string | undefined;
  role: string | undefined;
  isLoading: boolean;
  error: Error | null;
}

// Create the context with a more meaningful default value
const UserContext = createContext<UserContextProps>({
  email: undefined,
  role: undefined,
  isLoading: true, // Start with loading true
  error: null,
});

// Props for the provider component
interface UserProviderProps {
  children: ReactNode;
}

// Create the provider component with optimized loading
export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [email, setEmail] = useState<string | undefined>(undefined);
  const [role, setRole] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const config = useApi(configApiRef);
  const baseUrl = config.getString('backend.baseUrl');
  
  const identityApi = useApi(identityApiRef);
  
  useEffect(() => {
    // Immediately try to get profile info
    let isMounted = true;
    
    const fetchUserData = async () => {
      try {
        // Get the user profile info first to show email immediately
        const profile = await identityApi.getProfileInfo();
        if (!isMounted) return;
        
        const userEmail = profile.email;
        setEmail(userEmail);
        
        // Then get the role (this part can be slower)
        const { token } = await identityApi.getCredentials();
        if (!isMounted) return;
        
        // Fetch the user role from your API
        const response = await fetch(`${baseUrl}/api/users/Role`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ email: userEmail }),
        });

        if (!isMounted) return;

        if (!response.ok) {
          throw new Error(`Failed to fetch user role: ${response.statusText}`);
        }

        const userRoleResponse: UserRoleResponse = await response.json();
        setRole(userRoleResponse.roleName);
      } catch (error) {
        if (!isMounted) return;
        console.error('Error fetching user data:', error);
        setError(error instanceof Error ? error : new Error('Unknown error'));
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchUserData();
    
    return () => {
      isMounted = false;
    };
  }, [identityApi]);

  // Provide the context value
  const contextValue = {
    email,
    role,
    isLoading,
    error
  };

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
};

// Create a custom hook to use the context
export const useUserContext = () => useContext(UserContext);

// Add a simplified hook that doesn't require loading state to be handled
export const useUser = () => {
  const context = useContext(UserContext);
  return {
    email: context.email || '',
    role: context.role || '',
    isAuthenticated: Boolean(context.email),
    isLoading: context.isLoading
  };
};

export default UserContext;